import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Download,
  Trash2,
  Eye,
  Settings,
  Clock,
  FileText,
  AlertTriangle,
  ExternalLink,
  User,
  Mail,
  Fingerprint,
  Zap,
  Activity,
  History,
  Target,
  ChevronLeft,
  Cpu,
  Layers,
  Sparkles,
  Lock,
  Radar
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useToast } from '@/hooks/useToast';
import { gdprService } from '@/services/gdprService';
import type { PrivacySettings, ConsentType, PrivacyAuditLog } from '@/types/gdpr';

export function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success } = useToast();

  const [privacySettings, setPrivacySettings] = useState<PrivacySettings | null>(null);
  const [consent, setConsent] = useState<Record<ConsentType, boolean>>({
    essential: true,
    analytics: false,
    marketing: false,
    personalization: false,
    third_party: false,
    location: false,
    communications: false
  });
  const [auditLog, setAuditLog] = useState<PrivacyAuditLog[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'consent' | 'settings' | 'data' | 'activity'>('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      loadPrivacyData();
    }
  }, [user]);

  const loadPrivacyData = () => {
    if (!user) return;
    setPrivacySettings(gdprService.getPrivacySettings(user.id));
    setConsent(gdprService.getConsent(user.id));
    setAuditLog(gdprService.getAuditLog(user.id));
  };

  const updatePrivacySetting = (key: keyof PrivacySettings, value: any) => {
    if (!user || !privacySettings) return;
    const updated = { ...privacySettings, [key]: value };
    setPrivacySettings(updated);
    gdprService.updatePrivacySettings(user.id, { [key]: value });
    success('Privacy parameter updated');
  };

  const updateConsent = (type: ConsentType, granted: boolean) => {
    if (!user) return;
    setConsent(prev => ({ ...prev, [type]: granted }));
    gdprService.updateConsent(user.id, type, granted);
    success(`${type.split('_').join(' ')} settings updated successfully`);
  };

  const handleDataExport = () => {
    if (!user) return;
    success('Your data export has been initiated. You will receive a download link shortly.');
  };

  const handleAccountDeletion = () => {
    if (!user) return;
    gdprService.requestDataDeletion(user.id, 'User requested account deletion');
    success('Account deletion scheduled. Your account will be permanently deleted in 30 days.');
    setShowDeleteConfirm(false);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Privacy Settings' }
  ];

  const cookieCategories = gdprService.getCookieCategories();

  if (!user || !privacySettings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary-500/10 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  const tabItems = [
    { key: 'overview', label: 'Overview', icon: Eye, desc: 'Privacy Summary' },
    { key: 'consent', label: 'Consent', icon: Fingerprint, desc: 'Cookie Settings' },
    { key: 'settings', label: 'Settings', icon: Settings, desc: 'Privacy Controls' },
    { key: 'data', label: 'Data', icon: FileText, desc: 'Data Management' },
    { key: 'activity', label: 'Activity', icon: History, desc: 'Activity Log' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 selection:bg-primary-500/30 overflow-x-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      {/* Background Ambience */}
      <div className="fixed inset-0 -z-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-[1000px] h-[1000px] bg-primary-600/5 blur-[150px] rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-emerald-600/5 blur-[120px] rounded-full translate-x-1/2 translate-y-1/2" />
        <div className="absolute inset-0 bg-[url('/carbon-fibre.png')] opacity-20" />
      </div>

      <main id="main-content" role="main" className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-12">
        <div className="flex items-center justify-between mb-12">
          <Breadcrumb
            items={breadcrumbItems}
            onBack={() => navigate('/dashboard')}
          />
          <button
            onClick={() => navigate('/dashboard')}
            className="p-3 bg-white/5 border border-white/5 rounded-2xl hover:bg-white/10 transition-all group"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
          </button>
        </div>

        {/* Page Header */}
        <header className="mb-16 border-b border-gray-200 pb-10">
          <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-10">
            <div>
              <div className="flex items-center gap-6 mb-4">
                <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center shadow-sm">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                  Privacy Settings
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />
                  GDPR Compliant
                </span>
                <div className="w-px h-4 bg-gray-300" />
                <span>
                  Privacy Level: Secure
                </span>
              </div>
            </div>

            <div className="px-6 py-4 bg-white border border-gray-200 rounded-xl flex items-center gap-6 shadow-sm">
              <div className="flex flex-col items-end">
                <span className="text-xs font-medium text-gray-500 mb-1">Privacy Status</span>
                <span className="text-emerald-600 text-sm font-semibold">Active</span>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div className="flex flex-col items-end">
                <span className="text-xs font-medium text-gray-500 mb-1">System Uptime</span>
                <span className="text-gray-900 text-sm font-semibold">99.9%</span>
              </div>
            </div>
          </div>
        </header>

        {/* Navigation Tabs */}
        <nav className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16 p-2 bg-white border border-gray-200 rounded-xl shadow-sm">
          {tabItems.map(({ key, label, icon: Icon, desc }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`group relative flex flex-col items-center justify-center p-6 border-2 transition-all rounded-lg ${activeTab === key
                ? 'bg-primary-50 border-primary-500 shadow-sm'
                : 'bg-transparent border-transparent text-gray-600 hover:bg-gray-50'
                }`}
            >
              <Icon className={`w-6 h-6 mb-3 transition-all ${activeTab === key ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
              <div className="text-center">
                <div className={`text-sm font-semibold transition-colors ${activeTab === key ? 'text-gray-900' : 'text-gray-600'}`}>
                  {label}
                </div>
                <div className="text-xs text-gray-500 mt-1 hidden lg:block">
                  {desc}
                </div>
              </div>
            </button>
          ))}
        </nav>

        {/* Main Content */}
        <div className="relative min-h-[600px]">
          <div>
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Privacy Score */}
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-8 border-b border-gray-200 pb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-center">
                        <Shield className="w-6 h-6 text-emerald-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Privacy Score</h3>
                    </div>
                    <div className="text-right">
                      <span className="text-5xl font-bold text-emerald-600">85</span>
                      <span className="text-gray-400 text-sm ml-2">/100</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm leading-relaxed mb-8">
                    Your privacy settings are configured for maximum protection. All enhanced privacy filters are active.
                  </p>

                  <div className="space-y-4">
                    {[
                      { label: "Profile Visibility", val: privacySettings.profileVisibility, color: "primary-600" },
                      { label: "Data Retention", val: privacySettings.dataRetention, color: "emerald-600" },
                      { label: "Marketing Emails", val: privacySettings.marketingEmails ? 'Enabled' : 'Disabled', color: "gray-600" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 transition-all">
                        <span className="text-sm font-medium text-gray-700">{item.label}</span>
                        <span className={`text-sm font-semibold text-${item.color} capitalize`}>{item.val}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-4 mb-8 border-b border-gray-200 pb-6">
                    <div className="w-12 h-12 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Quick Actions</h3>
                  </div>

                  <div className="grid gap-4">
                    <button
                      onClick={handleDataExport}
                      className="w-full flex items-center justify-between p-5 bg-gray-50 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
                    >
                      <div className="flex items-center gap-4 text-left">
                        <Download className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-all" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Export Data</p>
                          <p className="text-xs text-gray-500 mt-0.5">Download your complete data archive</p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </button>

                    <button
                      onClick={() => setActiveTab('consent')}
                      className="w-full flex items-center justify-between p-5 bg-gray-50 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
                    >
                      <div className="flex items-center gap-4 text-left">
                        <Settings className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-all" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Cookie Settings</p>
                          <p className="text-xs text-gray-500 mt-0.5">Manage cookie preferences</p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </button>

                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full flex items-center justify-between p-5 bg-red-50 border border-red-200 rounded-lg hover:border-red-500 hover:bg-red-100 transition-all group"
                    >
                      <div className="flex items-center gap-4 text-left">
                        <Trash2 className="w-5 h-5 text-red-600 transition-all" />
                        <div>
                          <p className="text-sm font-semibold text-red-900">Delete Account</p>
                          <p className="text-xs text-red-600 mt-0.5">Permanently delete your account</p>
                        </div>
                      </div>
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'consent' && (
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-4 mb-8 border-b border-gray-200 pb-6">
                    <div className="w-12 h-12 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-center">
                      <Fingerprint className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Cookie Preferences</h3>
                      <p className="text-sm text-gray-600 mt-1">Manage your cookie and tracking settings</p>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    {cookieCategories.map((category) => (
                      <div key={category.id} className={`p-6 bg-gray-50 border rounded-lg transition-all ${consent[category.id] ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-base font-semibold text-gray-900">{category.name}</h4>
                              {category.essential && (
                                <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-700 border border-primary-200 rounded-full">
                                  Required
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">{category.description}</p>
                          </div>

                          <button
                            onClick={() => !category.essential && updateConsent(category.id, !consent[category.id])}
                            disabled={category.essential}
                            className={`w-16 h-8 rounded-full transition-all duration-500 p-1 relative ${consent[category.id] ? 'bg-primary-600 shadow-sm' : 'bg-gray-100'} ${category.essential ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                          >
                            <div className={`w-6 h-6 bg-white rounded-full transition-transform duration-500 shadow-xl ${consent[category.id] ? 'translate-x-8' : 'translate-x-0'}`} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-4 mb-8 border-b border-gray-200 pb-6">
                    <div className="w-12 h-12 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-center">
                      <User className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Privacy Controls</h3>
                  </div>

                  <div className="space-y-8">
                    <div className="max-w-xl">
                      <label className="text-sm font-medium text-gray-700 mb-3 block flex items-center gap-2">
                        <Target className="w-4 h-4 text-primary-600" />
                        Profile Visibility
                      </label>
                      <select
                        value={privacySettings.profileVisibility}
                        onChange={(e) => updatePrivacySetting('profileVisibility', e.target.value)}
                        className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 text-sm rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                      >
                        <option value="public">Public - Visible to everyone</option>
                        <option value="limited">Limited - Visible to connections only</option>
                        <option value="private">Private - Hidden from search</option>
                      </select>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {[
                        {
                          key: 'showLocation',
                          label: 'Show Location',
                          desc: 'Display your approximate location to customers',
                          icon: Radar
                        },
                        {
                          key: 'allowDirectContact',
                          label: 'Direct Contact',
                          desc: 'Allow customers to contact you directly',
                          icon: Mail
                        },
                        {
                          key: 'anonymousReviews',
                          label: 'Anonymous Reviews',
                          desc: 'Keep your reviews anonymous',
                          icon: Eye
                        },
                        {
                          key: 'marketingEmails',
                          label: 'Marketing Emails',
                          desc: 'Receive updates and promotional emails',
                          icon: Zap
                        }
                      ].map((item) => (
                        <div key={item.key} className="p-6 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between hover:border-primary-500 transition-all">
                          <div className="flex gap-4 items-start">
                            <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center text-gray-600">
                              <item.icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">{item.label}</h4>
                              <p className="text-xs text-gray-500 mt-1 max-w-[200px]">{item.desc}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => updatePrivacySetting(item.key as any, !privacySettings[item.key as keyof PrivacySettings])}
                            className={`w-12 h-6 rounded-full transition-all p-1 relative ${privacySettings[item.key as keyof PrivacySettings] ? 'bg-emerald-600' : 'bg-gray-300'}`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${privacySettings[item.key as keyof PrivacySettings] ? 'translate-x-6' : 'translate-x-0'}`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'data' && (
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-4 mb-8 border-b border-gray-200 pb-6">
                    <div className="w-12 h-12 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-center">
                      <Layers className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Data Management</h3>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg">
                      <Download className="w-10 h-10 text-primary-600 mb-6" />
                      <h4 className="text-xl font-bold text-gray-900 mb-3">Export Data</h4>
                      <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                        Request a complete export of all your personal data and account information.
                      </p>
                      <button
                        onClick={handleDataExport}
                        className="w-full py-3 bg-primary-600 text-white font-medium text-sm rounded-lg hover:bg-primary-700 transition-all"
                      >
                        Request Export
                      </button>
                    </div>

                    <div className="p-8 bg-red-50 border border-red-200 rounded-lg">
                      <Trash2 className="w-10 h-10 text-red-600 mb-6" />
                      <h4 className="text-xl font-bold text-red-900 mb-3">Delete Account</h4>
                      <p className="text-sm text-red-700 mb-8 leading-relaxed">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="w-full py-3 bg-transparent border-2 border-red-600 text-red-600 font-medium text-sm rounded-lg hover:bg-red-600 hover:text-white transition-all"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-4 mb-8 border-b border-gray-200 pb-6">
                    <div className="w-12 h-12 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-center">
                      <History className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Activity Log</h3>
                  </div>

                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {auditLog.length === 0 ? (
                      <p className="text-gray-500 text-center py-20 text-sm">No activity recorded</p>
                    ) : (
                      auditLog.map((log) => (
                        <div key={log.id} className="flex items-center gap-6 p-5 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 transition-all">
                          <div className="w-px h-12 bg-primary-500 hidden md:block" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-gray-900">
                                {log.action.replace('_', ' ')}
                              </h4>
                              <span className="text-xs text-gray-500">
                                {log.timestamp.toLocaleDateString()} {log.timestamp.toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">{log.details}</p>
                          </div>
                          <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                            <Cpu className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
            <div className="bg-white p-12 w-full max-w-xl rounded-xl border border-gray-200 shadow-2xl">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-red-50 border-2 border-dashed border-red-300 rounded-full flex items-center justify-center mb-8">
                  <AlertTriangle className="w-12 h-12 text-red-600" />
                </div>

                <h3 className="text-3xl font-bold text-gray-900 mb-4">Delete Account</h3>

                <p className="text-gray-600 text-sm mb-8 leading-relaxed max-w-md">
                  This action is irreversible. Your account and all associated data will be permanently deleted. This cannot be undone.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="py-3 bg-transparent border-2 border-gray-300 text-gray-700 font-medium text-sm rounded-lg hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAccountDeletion}
                    className="py-3 bg-red-600 text-white font-medium text-sm rounded-lg hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Confirm Deletion
                  </button>
                </div>

                <p className="mt-6 text-xs text-gray-500">Awaiting confirmation</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}